/**
 * <s-hero-rotator> — Auto-advancing image rotator for the mobile hero. No swipe, no pause control.
 *
 * Markup contract:
 *   [data-rotator-track]  — flex row of slides, moved with translateX
 *   [data-rotator-slide]  — one per image
 *   [data-rotator-dot]    — indicator buttons; the active one stretches into a pill and fills while its slide shows
 *
 * Attributes:
 *   data-interval — milliseconds per slide (default 5000)
 *
 * Pauses while the page is hidden or the hero is off screen. Visitors who prefer reduced
 * motion get no autoplay; the dots still change the image without sliding.
 */
class SHeroRotator extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #dots = [];
  #index = 0;
  #timer = 0;
  #visible = true;
  #observer;

  connectedCallback() {
    this.#track = this.querySelector('[data-rotator-track]');
    this.#slides = Array.from(this.querySelectorAll('[data-rotator-slide]'));
    this.#dots = Array.from(this.querySelectorAll('[data-rotator-dot]'));
    if (!this.#track || this.#slides.length < 2) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#dots.forEach((dot, i) => dot.addEventListener('click', () => this.#go(i), opts));
    document.addEventListener('visibilitychange', () => this.#schedule(), opts);

    this.#observer = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      this.#schedule();
    });
    this.#observer.observe(this);

    this.#go(0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
  }

  get #interval() {
    return Number(this.dataset.interval) || 5000;
  }

  get #reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #go(index) {
    const count = this.#slides.length;
    this.#index = ((index % count) + count) % count;
    const isRtl = getComputedStyle(this).direction === 'rtl';
    this.#track.style.transform = `translateX(${this.#index * (isRtl ? 100 : -100)}%)`;

    this.#slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.#dots.forEach((dot, i) => {
      const active = i === this.#index;
      dot.toggleAttribute('data-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
      // Restart the fill animation on the newly active dot.
      if (active) {
        dot.style.setProperty('--rotator-interval', `${this.#interval}ms`);
        dot.removeAttribute('data-filling');
        void dot.offsetWidth;
        if (!this.#reduced) dot.setAttribute('data-filling', '');
      } else {
        dot.removeAttribute('data-filling');
      }
    });

    this.#schedule();
  }

  #schedule() {
    clearTimeout(this.#timer);
    if (this.#reduced || document.hidden || !this.#visible) return;
    this.#timer = setTimeout(() => this.#go(this.#index + 1), this.#interval);
  }
}

if (!customElements.get('s-hero-rotator')) {
  customElements.define('s-hero-rotator', SHeroRotator);
}
